import { DragEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';

import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { ErrorBoxComponent } from 'components/Error/ErrorBoxComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { useGetFunctionDefinitionCubeDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import { useGetAccountabilityDefinitions } from 'services/v1/SystemTenant/AROChart/AccountabilityDefinitionService';
import {
  useCreateKpiDefinition,
  useGetKpiDefinition,
  useGetKpiDefinitionSourceColumns,
  useGetKpiInsightViewColumns,
  useUpdateKpiDefinition,
} from 'services/v1/SystemTenant/AROChart/KpiDefinitionService';
import { useGetInsightViewDefinitionDetails } from 'services/v1/SystemTenant/AROView/InsightViewDefinitionService';
import { useGetCubeInsightViewDropdown } from 'services/v1/Tenant/AROCube/CubeInsightViewService';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  KpiCalculationColumnType,
  KpiDefinitionRequest,
  KpiDefinitionSourceColumn,
  KpiInsightViewColumn,
} from 'types/api/SystemTenant/AROChart/KpiDefinitionTypes';

import { KpiChecklistModal, KpiChecklistModalRow } from './components/KpiChecklistModal';

const sectionCardSx = {
  bgcolor: '#fff',
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
  p: 2.5,
};

const autocompleteSx = {
  '& .MuiOutlinedInput-root': {
    py: 0,
  },
};

interface SelectedKpiColumn extends KpiDefinitionSourceColumn {
  primary: boolean;
}

type SelectedCalculationColumn = KpiInsightViewColumn;

interface KpiDefinitionFormValue {
  name: string;
  cube: AutoCompleteItem | null;
  insightView: AutoCompleteItem | null;
  accountabilityDefinition: AutoCompleteItem | null;
  calculationColumns: SelectedCalculationColumn[];
  attributes: SelectedKpiColumn[];
}

const initialValues: KpiDefinitionFormValue = {
  name: '',
  cube: null,
  insightView: null,
  accountabilityDefinition: null,
  calculationColumns: [],
  attributes: [],
};

function getAttributeKey(column: Pick<KpiDefinitionSourceColumn, 'cubeAttributeId'>) {
  return column.cubeAttributeId;
}

function getCalculationColumnKey(column: Pick<KpiInsightViewColumn, 'calculationColumnType' | 'sourceId'>) {
  return `${column.calculationColumnType}:${column.sourceId}`;
}

function formatCalculationColumnType(calculationColumnType: KpiCalculationColumnType) {
  return calculationColumnType
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function isSameOption(option: AutoCompleteItem, value: AutoCompleteItem) {
  return option.value.toString() === value.value.toString();
}

interface KpiDefinitionFormProps {
  definitionId?: string;
}

type KpiOrderedCollection = 'calculationColumns' | 'attributes';

interface KpiDragState {
  collection: KpiOrderedCollection;
  sourceIndex: number;
  overIndex: number;
}

function reorderItems<T>(items: T[], sourceIndex: number, targetIndex: number) {
  if (
    sourceIndex === targetIndex ||
    sourceIndex < 0 ||
    targetIndex < 0 ||
    sourceIndex >= items.length ||
    targetIndex >= items.length
  ) {
    return items;
  }

  const reorderedItems = [...items];
  const [movedItem] = reorderedItems.splice(sourceIndex, 1);
  reorderedItems.splice(targetIndex, 0, movedItem);
  return reorderedItems;
}

export default function KpiDefinitionForm({ definitionId }: KpiDefinitionFormProps) {
  const navigate = useNavigate();
  const isEditing = Boolean(definitionId);
  const [values, setValues] = useState<KpiDefinitionFormValue>(initialValues);
  const [submitted, setSubmitted] = useState(false);
  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);
  const [isAttributeModalOpen, setIsAttributeModalOpen] = useState(false);
  const [dragState, setDragState] = useState<KpiDragState | null>(null);

  const {
    data: definitionDetails,
    isLoading: isLoadingDefinition,
    isError: isDefinitionError,
    error: definitionError,
  } = useGetKpiDefinition(definitionId);
  const {
    data: accountabilityDefinitions,
    isLoading: isLoadingAccountabilityDefinitions,
    isError: isAccountabilityDefinitionsError,
    error: accountabilityDefinitionsError,
  } = useGetAccountabilityDefinitions();
  const {
    data: cubeDropdown,
    isLoading: isLoadingCubes,
    isError: isCubeError,
    error: cubeError,
  } = useGetFunctionDefinitionCubeDropdown();
  const selectedCubeId = values.cube?.value.toString() ?? '';
  const {
    data: insightViewDropdown,
    isLoading: isLoadingInsightViews,
    isError: isInsightViewError,
    error: insightViewError,
  } = useGetCubeInsightViewDropdown(selectedCubeId);
  const insightViewDefinitionId = values.insightView?.value.toString();
  const existingInsightViewId = definitionDetails?.data.insightView.value;
  const {
    data: existingInsightViewDetails,
    isLoading: isLoadingExistingInsightView,
    isError: isExistingInsightViewError,
    error: existingInsightViewError,
  } = useGetInsightViewDefinitionDetails(isEditing ? existingInsightViewId : null);
  const {
    data: insightViewColumns,
    isLoading: isLoadingInsightViewColumns,
    isError: isInsightViewColumnsError,
    error: insightViewColumnsError,
  } = useGetKpiInsightViewColumns(insightViewDefinitionId);
  const {
    data: sourceColumns,
    isLoading: isLoadingSourceColumns,
    isError: isSourceColumnsError,
    error: sourceColumnsError,
  } = useGetKpiDefinitionSourceColumns(insightViewDefinitionId);
  const { mutate: createDefinition, isLoading: isCreating } = useCreateKpiDefinition();
  const { mutate: updateDefinition, isLoading: isUpdating } = useUpdateKpiDefinition();

  useEffect(() => {
    if (!definitionDetails?.data || !existingInsightViewDetails?.data || !cubeDropdown?.data.data) return;

    const definition = definitionDetails.data;
    const cube = cubeDropdown.data.data.find(
      (cubeOption) => cubeOption.value.toString() === existingInsightViewDetails.data.cube.value.toString()
    );
    setValues({
      name: definition.name,
      cube: cube ?? null,
      insightView: definition.insightView,
      accountabilityDefinition: definition.accountabilityDefinition,
      calculationColumns: [...definition.calculationColumns]
        .sort((left, right) => left.order - right.order)
        .map(({ calculationColumnType, sourceId, columnId, displayName, group }) => ({
          calculationColumnType,
          sourceId,
          columnId,
          displayName,
          group,
        })),
      attributes: [...definition.attributes]
        .sort((left, right) => left.order - right.order)
        .map(({ cubeAttributeId, attributeId, referenceCode, displayName, primary }) => ({
          cubeAttributeId,
          attributeId,
          referenceCode,
          displayName,
          primary,
        })),
    });
  }, [cubeDropdown?.data.data, definitionDetails, existingInsightViewDetails]);

  const insightViewOptions = useMemo(() => {
    const options = insightViewDropdown?.data ?? [];
    const selectedInsightView = values.insightView;
    if (!selectedInsightView || options.some((option) => isSameOption(option, selectedInsightView))) return options;
    return [selectedInsightView, ...options];
  }, [insightViewDropdown?.data, values.insightView]);

  const accountabilityDefinitionOptions = useMemo(() => {
    const options = (accountabilityDefinitions?.data ?? [])
      .map((definition) => ({
        value: definition.id,
        label: definition.name,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
    const selectedDefinition = values.accountabilityDefinition;
    if (!selectedDefinition || options.some((option) => isSameOption(option, selectedDefinition))) {
      return options;
    }
    return [selectedDefinition, ...options];
  }, [accountabilityDefinitions?.data, values.accountabilityDefinition]);

  const availableCalculationColumns = useMemo(() => {
    const selectedKeys = new Set(values.calculationColumns.map(getCalculationColumnKey));
    return [...(insightViewColumns?.data ?? [])]
      .filter((column) => !selectedKeys.has(getCalculationColumnKey(column)))
      .sort(
        (left, right) =>
          (left.group?.label ?? '').localeCompare(right.group?.label ?? '') ||
          left.displayName.localeCompare(right.displayName)
      );
  }, [insightViewColumns?.data, values.calculationColumns]);

  const availableSourceColumns = useMemo(() => {
    const selectedKeys = new Set(values.attributes.map(getAttributeKey));
    return [...(sourceColumns?.data ?? [])]
      .filter((column) => !selectedKeys.has(getAttributeKey(column)))
      .sort((left, right) => left.displayName.localeCompare(right.displayName));
  }, [sourceColumns?.data, values.attributes]);

  const calculationModalRows = useMemo<KpiChecklistModalRow[]>(
    () =>
      availableCalculationColumns.map((column) => ({
        id: getCalculationColumnKey(column),
        displayName: column.displayName,
        calculationType: formatCalculationColumnType(column.calculationColumnType),
        groupName: column.group?.label ?? 'Other',
      })),
    [availableCalculationColumns]
  );

  const attributeModalRows = useMemo<KpiChecklistModalRow[]>(
    () =>
      availableSourceColumns.map((column) => ({
        id: getAttributeKey(column),
        displayName: column.displayName,
        referenceCode: column.referenceCode,
      })),
    [availableSourceColumns]
  );

  const primaryColumnCount = values.attributes.filter((attribute) => attribute.primary).length;
  const isFormValid =
    Boolean(values.name.trim()) &&
    Boolean(values.cube) &&
    Boolean(values.insightView) &&
    Boolean(values.accountabilityDefinition) &&
    values.calculationColumns.length > 0 &&
    values.attributes.length > 0 &&
    primaryColumnCount === 1;

  const handleDragStart = (
    event: DragEvent<HTMLDivElement>,
    collection: KpiOrderedCollection,
    sourceIndex: number
  ) => {
    setDragState({ collection, sourceIndex, overIndex: sourceIndex });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${collection}:${sourceIndex}`);
  };

  const handleDragOver = (
    event: DragEvent<HTMLDivElement>,
    collection: KpiOrderedCollection,
    overIndex: number
  ) => {
    if (dragState?.collection !== collection) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragState.overIndex !== overIndex) {
      setDragState((current) => (current ? { ...current, overIndex } : current));
    }
  };

  const handleDrop = (
    event: DragEvent<HTMLDivElement>,
    collection: KpiOrderedCollection,
    targetIndex: number
  ) => {
    event.preventDefault();
    if (!dragState || dragState.collection !== collection) return;

    setValues((current) =>
      collection === 'calculationColumns'
        ? {
            ...current,
            calculationColumns: reorderItems(current.calculationColumns, dragState.sourceIndex, targetIndex),
          }
        : {
            ...current,
            attributes: reorderItems(current.attributes, dragState.sourceIndex, targetIndex),
          }
    );
    setDragState(null);
  };

  const handleDragEnd = () => setDragState(null);

  const addCalculationColumns = (selectedIds: string[]) => {
    const selectedKeys = new Set(selectedIds);
    const selectedColumns = availableCalculationColumns.filter((column) =>
      selectedKeys.has(getCalculationColumnKey(column))
    );
    if (selectedColumns.length === 0) return;

    setValues((current) => ({
      ...current,
      calculationColumns: [...current.calculationColumns, ...selectedColumns],
    }));
  };

  const removeCalculationColumn = (index: number) => {
    setValues((current) => ({
      ...current,
      calculationColumns: current.calculationColumns.filter((_, columnIndex) => columnIndex !== index),
    }));
  };

  const moveCalculationColumn = (index: number, direction: -1 | 1) => {
    const destinationIndex = index + direction;
    if (destinationIndex < 0 || destinationIndex >= values.calculationColumns.length) return;

    setValues((current) => ({
      ...current,
      calculationColumns: reorderItems(current.calculationColumns, index, destinationIndex),
    }));
  };

  const addColumns = (selectedIds: string[]) => {
    const selectedKeys = new Set(selectedIds);
    const selectedColumns = availableSourceColumns.filter((column) => selectedKeys.has(getAttributeKey(column)));
    if (selectedColumns.length === 0) return;

    setValues((current) => ({
      ...current,
      attributes: [
        ...current.attributes,
        ...selectedColumns.map((column, index) => ({
          ...column,
          primary: current.attributes.length === 0 && index === 0,
        })),
      ],
    }));
  };

  const removeColumn = (index: number) => {
    setValues((current) => {
      const removedColumnWasPrimary = current.attributes[index]?.primary;
      const attributes = current.attributes.filter((_, attributeIndex) => attributeIndex !== index);

      if (removedColumnWasPrimary && attributes.length > 0) {
        attributes[0] = { ...attributes[0], primary: true };
      }

      return { ...current, attributes };
    });
  };

  const moveColumn = (index: number, direction: -1 | 1) => {
    const destinationIndex = index + direction;
    if (destinationIndex < 0 || destinationIndex >= values.attributes.length) return;

    setValues((current) => ({
      ...current,
      attributes: reorderItems(current.attributes, index, destinationIndex),
    }));
  };

  const selectPrimaryColumn = (index: number) => {
    setValues((current) => ({
      ...current,
      attributes: current.attributes.map((attribute, attributeIndex) => ({
        ...attribute,
        primary: attributeIndex === index,
      })),
    }));
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!isFormValid || !values.insightView || !values.accountabilityDefinition) return;

    const payload: KpiDefinitionRequest = {
      name: values.name.trim(),
      insightViewDefinitionId: values.insightView.value.toString(),
      accountabilityDefinitionId: values.accountabilityDefinition.value.toString(),
      calculationColumns: values.calculationColumns.map((column, order) => ({
        calculationColumnType: column.calculationColumnType,
        sourceId: column.sourceId,
        order,
      })),
      attributes: values.attributes.map((attribute, order) => ({
        cubeAttributeId: attribute.cubeAttributeId,
        primary: attribute.primary,
        order,
      })),
    };

    const onSuccess = () => {
      toast.success(`KPI definition ${isEditing ? 'updated' : 'created'} successfully`);
      navigate(PATH_CONSTANT.SYSTEM.CHART.KPI_DEFINITION);
    };

    if (definitionId) {
      updateDefinition({ id: definitionId, data: payload }, { onSuccess });
    } else {
      createDefinition(payload, { onSuccess });
    }
  };

  const isLoading =
    isLoadingDefinition ||
    isLoadingCubes ||
    isLoadingExistingInsightView ||
    isLoadingAccountabilityDefinitions;
  const isSaving = isCreating || isUpdating;
  const loadError =
    definitionError ??
    cubeError ??
    existingInsightViewError ??
    accountabilityDefinitionsError ??
    insightViewError ??
    insightViewColumnsError ??
    sourceColumnsError;
  const hasLoadError =
    isDefinitionError ||
    isCubeError ||
    isExistingInsightViewError ||
    isAccountabilityDefinitionsError ||
    isInsightViewError ||
    isInsightViewColumnsError ||
    isSourceColumnsError;
  const title = isEditing ? 'Edit KPI Definition' : 'Add KPI Definition';

  return (
    <MainLayoutComponent pageTitle={title} breadcrumbs={['Dashboard', 'KPI Definition']}>
      {hasLoadError ? (
        <ErrorBoxComponent error={loadError} />
      ) : isLoading ? (
        <Stack height={450} alignItems='center' justifyContent='center' bgcolor='#fff'>
          <ActivityIndicator />
        </Stack>
      ) : (
        <Stack gap={2} p={2} bgcolor='#F8FAFF'>
          {submitted && !isFormValid && (
            <Alert severity='error'>
              Enter a name, select a Cube, Insight View, and Accountability Definition, add at least one calculation
              column and attribute, and choose one primary attribute.
            </Alert>
          )}

          <Box sx={sectionCardSx}>
            <Typography variant='body1' component='h2' fontWeight='bold' color='#3B4797'>
              Definition
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={0.25}>
              Name the KPI, select its source, and associate an Accountability Definition.
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'repeat(2, minmax(220px, 1fr))',
                  xl: 'repeat(4, minmax(220px, 1fr))',
                },
                gap: 2,
              }}
            >
              <TextField
                label='Name'
                size='small'
                value={values.name}
                onChange={(event) => setValues((current) => ({ ...current, name: event.target.value }))}
                error={submitted && !values.name.trim()}
                helperText={submitted && !values.name.trim() ? 'Enter a KPI definition name' : undefined}
                placeholder='e.g. Financial Performance'
              />

              <Autocomplete
                options={cubeDropdown?.data.data ?? []}
                value={values.cube}
                isOptionEqualToValue={isSameOption}
                getOptionLabel={(option) => option.label}
                onChange={(_, cube) =>
                  setValues((current) => ({
                    ...current,
                    cube,
                    insightView: null,
                    calculationColumns: [],
                    attributes: [],
                  }))
                }
                sx={autocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Cube'
                    size='small'
                    error={submitted && !values.cube}
                    helperText={submitted && !values.cube ? 'Select a Cube' : undefined}
                    placeholder='Search Cubes'
                  />
                )}
              />

              <Autocomplete
                options={insightViewOptions}
                value={values.insightView}
                disabled={!values.cube}
                loading={isLoadingInsightViews}
                isOptionEqualToValue={isSameOption}
                getOptionLabel={(option) => option.label}
                onChange={(_, insightView) =>
                  setValues((current) => ({
                    ...current,
                    insightView,
                    calculationColumns: [],
                    attributes: [],
                  }))
                }
                sx={autocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Insight View'
                    size='small'
                    error={submitted && !values.insightView}
                    helperText={
                      submitted && !values.insightView
                        ? 'Select an Insight View'
                        : values.cube && !isLoadingInsightViews && insightViewOptions.length === 0
                        ? 'No Insight Views are available for this Cube'
                        : undefined
                    }
                    placeholder={values.cube ? 'Search Insight Views' : 'Select a Cube first'}
                  />
                )}
              />

              <Autocomplete
                options={accountabilityDefinitionOptions}
                value={values.accountabilityDefinition}
                loading={isLoadingAccountabilityDefinitions}
                isOptionEqualToValue={isSameOption}
                getOptionLabel={(option) => option.label}
                onChange={(_, accountabilityDefinition) =>
                  setValues((current) => ({
                    ...current,
                    accountabilityDefinition,
                  }))
                }
                sx={autocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Accountability Definition'
                    size='small'
                    error={submitted && !values.accountabilityDefinition}
                    helperText={
                      submitted && !values.accountabilityDefinition
                        ? 'Select an Accountability Definition'
                        : accountabilityDefinitionOptions.length === 0
                        ? 'No Accountability Definitions are available'
                        : undefined
                    }
                    placeholder='Search Accountability Definitions'
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={sectionCardSx}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' gap={1.5}>
              <Box>
                <Typography variant='body1' component='h2' fontWeight='bold' color='#3B4797'>
                  Calculation Columns
                </Typography>
                <Typography variant='body2' color='text.secondary' mt={0.25}>
                  Select the Insight View columns used to calculate this KPI, then drag them into calculation order.
                </Typography>
              </Box>
              <Button
                variant='main-table-panel'
                startIcon={<AddCircleOutlineIcon />}
                disabled={
                  !insightViewDefinitionId || isLoadingInsightViewColumns || availableCalculationColumns.length === 0
                }
                onClick={() => setIsCalculationModalOpen(true)}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
              >
                Add
              </Button>
            </Stack>
            <Divider sx={{ my: 2 }} />

            {!insightViewDefinitionId ? (
              <Box sx={{ border: '1px dashed #CCD8E8', borderRadius: 1.5, py: 4, textAlign: 'center' }}>
                <Typography color='text.secondary'>Select an Insight View to load its calculation columns.</Typography>
              </Box>
            ) : values.calculationColumns.length === 0 ? (
              <Box sx={{ border: '1px dashed #CCD8E8', borderRadius: 1.5, py: 4, textAlign: 'center' }}>
                <AddCircleOutlineIcon sx={{ color: '#9BA9BD', mb: 0.5 }} />
                <Typography color='text.secondary'>Click Add to select one or more calculation columns.</Typography>
              </Box>
            ) : (
              <Stack gap={1.25}>
                {values.calculationColumns.map((column, index) => (
                  <Box
                    key={getCalculationColumnKey(column)}
                    onDragOver={(event) => handleDragOver(event, 'calculationColumns', index)}
                    onDrop={(event) => handleDrop(event, 'calculationColumns', index)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '80px minmax(180px, 1fr) minmax(150px, 0.65fr) auto',
                      },
                      gap: 1.5,
                      alignItems: 'center',
                      border:
                        dragState?.collection === 'calculationColumns' && dragState.overIndex === index
                          ? '2px solid #3B4797'
                          : '1px solid #DCE6F3',
                      borderRadius: 1.5,
                      bgcolor:
                        dragState?.collection === 'calculationColumns' && dragState.sourceIndex === index
                          ? '#F0F3FA'
                          : '#FBFCFF',
                      opacity:
                        dragState?.collection === 'calculationColumns' && dragState.sourceIndex === index ? 0.55 : 1,
                      px: 1.5,
                      py: 1.25,
                      transition: 'border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
                    }}
                  >
                    <Stack direction='row' alignItems='center' gap={0.75}>
                      <Tooltip title='Drag to reorder. Use the arrow keys when focused.'>
                        <Box
                          draggable
                          role='button'
                          tabIndex={0}
                          aria-label={`Reorder calculation column ${column.displayName}`}
                          onDragStart={(event) => handleDragStart(event, 'calculationColumns', index)}
                          onDragEnd={handleDragEnd}
                          onKeyDown={(event) => {
                            if (event.key === 'ArrowUp' && index > 0) {
                              event.preventDefault();
                              moveCalculationColumn(index, -1);
                            } else if (
                              event.key === 'ArrowDown' &&
                              index < values.calculationColumns.length - 1
                            ) {
                              event.preventDefault();
                              moveCalculationColumn(index, 1);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            color: '#98A2AE',
                            cursor: 'grab',
                            borderRadius: 0.75,
                            '&:active': { cursor: 'grabbing' },
                            '&:hover': { color: '#3B4797' },
                            '&:focus-visible': { outline: '2px solid #7D89D8', outlineOffset: 2 },
                          }}
                        >
                          <DragIndicatorRoundedIcon />
                        </Box>
                      </Tooltip>
                      <Typography
                        sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          bgcolor: '#E9EDFA',
                          color: '#3B4797',
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </Stack>

                    <Box minWidth={0}>
                      <Typography fontWeight={700} noWrap>
                        {column.displayName}
                      </Typography>
                      <Stack direction='row' gap={0.75} alignItems='center' mt={0.5} flexWrap='wrap'>
                        <Chip
                          size='small'
                          label={formatCalculationColumnType(column.calculationColumnType)}
                          sx={{ height: 22 }}
                        />
                      </Stack>
                    </Box>

                    <Typography variant='body2' color='text.secondary'>
                      {column.group?.label ?? 'Other'}
                    </Typography>

                    <Stack direction='row' alignItems='center' justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                      <Tooltip title='Remove calculation column'>
                        <IconButton size='small' color='error' onClick={() => removeCalculationColumn(index)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            {insightViewDefinitionId && !isLoadingInsightViewColumns && availableCalculationColumns.length === 0 && (
              <Typography variant='caption' color='text.secondary' display='block' mt={1}>
                All available calculation columns have been added.
              </Typography>
            )}
          </Box>

          <Box sx={sectionCardSx}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' gap={1.5}>
              <Box>
                <Typography variant='body1' component='h2' fontWeight='bold' color='#3B4797'>
                  KPI Attributes
                </Typography>
                <Typography variant='body2' color='text.secondary' mt={0.25}>
                  Add Cube Attributes, choose the primary attribute, then drag them into display order.
                </Typography>
              </Box>
              <Button
                variant='main-table-panel'
                startIcon={<AddCircleOutlineIcon />}
                disabled={!insightViewDefinitionId || isLoadingSourceColumns || availableSourceColumns.length === 0}
                onClick={() => setIsAttributeModalOpen(true)}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
              >
                Add
              </Button>
            </Stack>
            <Divider sx={{ my: 2 }} />

            {!insightViewDefinitionId ? (
              <Box sx={{ border: '1px dashed #CCD8E8', borderRadius: 1.5, py: 4, textAlign: 'center' }}>
                <Typography color='text.secondary'>Select an Insight View to load its available columns.</Typography>
              </Box>
            ) : values.attributes.length === 0 ? (
              <Box sx={{ border: '1px dashed #CCD8E8', borderRadius: 1.5, py: 4, textAlign: 'center' }}>
                <AddCircleOutlineIcon sx={{ color: '#9BA9BD', mb: 0.5 }} />
                <Typography color='text.secondary'>Click Add to select one or more Cube Attributes.</Typography>
              </Box>
            ) : (
              <Stack gap={1.25}>
                {values.attributes.map((attribute, index) => (
                  <Box
                    key={getAttributeKey(attribute)}
                    onDragOver={(event) => handleDragOver(event, 'attributes', index)}
                    onDrop={(event) => handleDrop(event, 'attributes', index)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '80px minmax(180px, 1fr) auto',
                      },
                      gap: 1.5,
                      alignItems: 'center',
                      border:
                        dragState?.collection === 'attributes' && dragState.overIndex === index
                          ? '2px solid #3B4797'
                          : attribute.primary
                          ? '1px solid #7D89D8'
                          : '1px solid #DCE6F3',
                      borderRadius: 1.5,
                      bgcolor:
                        dragState?.collection === 'attributes' && dragState.sourceIndex === index
                          ? '#F0F3FA'
                          : attribute.primary
                          ? '#F7F7FF'
                          : '#FBFCFF',
                      opacity: dragState?.collection === 'attributes' && dragState.sourceIndex === index ? 0.55 : 1,
                      px: 1.5,
                      py: 1.25,
                      transition: 'border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
                    }}
                  >
                    <Stack direction='row' alignItems='center' gap={0.75}>
                      <Tooltip title='Drag to reorder. Use the arrow keys when focused.'>
                        <Box
                          draggable
                          role='button'
                          tabIndex={0}
                          aria-label={`Reorder KPI attribute ${attribute.displayName}`}
                          onDragStart={(event) => handleDragStart(event, 'attributes', index)}
                          onDragEnd={handleDragEnd}
                          onKeyDown={(event) => {
                            if (event.key === 'ArrowUp' && index > 0) {
                              event.preventDefault();
                              moveColumn(index, -1);
                            } else if (event.key === 'ArrowDown' && index < values.attributes.length - 1) {
                              event.preventDefault();
                              moveColumn(index, 1);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            color: '#98A2AE',
                            cursor: 'grab',
                            borderRadius: 0.75,
                            '&:active': { cursor: 'grabbing' },
                            '&:hover': { color: '#3B4797' },
                            '&:focus-visible': { outline: '2px solid #7D89D8', outlineOffset: 2 },
                          }}
                        >
                          <DragIndicatorRoundedIcon />
                        </Box>
                      </Tooltip>
                      <Typography
                        sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          bgcolor: '#E9EDFA',
                          color: '#3B4797',
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </Stack>

                    <Box minWidth={0}>
                      <Typography fontWeight={700} noWrap>
                        {attribute.displayName}
                      </Typography>
                      <Stack direction='row' gap={0.75} alignItems='center' mt={0.5} flexWrap='wrap'>
                        <Chip size='small' label={attribute.referenceCode} sx={{ height: 22 }} />
                      </Stack>
                    </Box>

                    <Stack direction='row' alignItems='center' justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                      <FormControlLabel
                        control={
                          <Radio size='small' checked={attribute.primary} onChange={() => selectPrimaryColumn(index)} />
                        }
                        label='Primary'
                        sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                      />
                      <Tooltip title='Remove column'>
                        <IconButton size='small' color='error' onClick={() => removeColumn(index)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            {insightViewDefinitionId && !isLoadingSourceColumns && availableSourceColumns.length === 0 && (
              <Typography variant='caption' color='text.secondary' display='block' mt={1}>
                All available source columns have been added.
              </Typography>
            )}
          </Box>

          <KpiChecklistModal
            open={isCalculationModalOpen}
            title='Add Calculation Columns'
            description='Select one or more Insight View calculation columns to add.'
            type='calculation'
            rows={calculationModalRows}
            loading={isLoadingInsightViewColumns}
            onClose={() => setIsCalculationModalOpen(false)}
            onAdd={addCalculationColumns}
          />

          <KpiChecklistModal
            open={isAttributeModalOpen}
            title='Add KPI Attributes'
            description='Select one or more Cube Attributes to add to this KPI definition.'
            type='attribute'
            rows={attributeModalRows}
            loading={isLoadingSourceColumns}
            onClose={() => setIsAttributeModalOpen(false)}
            onAdd={addColumns}
          />

          <Box sx={{ ...sectionCardSx, display: 'flex', justifyContent: 'flex-end' }}>
            <Stack direction='row' gap={1.5}>
              <Button
                variant='main-table-panel-border'
                disabled={isSaving}
                onClick={() => navigate(PATH_CONSTANT.SYSTEM.CHART.KPI_DEFINITION)}
              >
                Cancel
              </Button>
              <LoadingButton variant='main-table-panel' loading={isSaving} onClick={handleSave}>
                Save Definition
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      )}
    </MainLayoutComponent>
  );
}
